// Defaults & Workout rebuild — pure client-side aggregation of every case
// artifact into ONE chronological timeline spine. No new backend endpoint:
// the workspace fetches each artifact list from a read that already exists;
// this module just folds them into typed entries (newest first) the drawer
// can resolve.
//
// Report Requests joins the fold on the same terms. `GET /portfolio-admin/
// loans/{id}/report-requests` already serves the loan's Financial Reporting
// tab, so the workspace adds one more of the reads it was already making
// rather than anything new, and the narrowing to THIS case happens here —
// see `reportRequestsForCase` for why it is not left to the caller.

import {
  formatCalendarDate,
  formatCurrency,
  isPastCalendarDay,
} from "@/design-system/lib/format";
import type {
  CollateralRecoveryDto,
  CurePeriodDto,
  DACAEventDto,
  DefaultCaseDto,
  EnforcerEngagementDto,
  ForbearanceAgreementDto,
  LegalActionDto,
  RemediationPlanDto,
  WorkoutCostEntryDto,
  WorkoutEventDto,
  WorkoutRecoveryEntryDto,
} from "@/api/portfolioAdmin/pr4bTypes";
import {
  REPORT_REQUEST_STATUS_LABEL,
  TERMINAL_REQUEST_STATUSES,
  type ReportRequestDto,
} from "@/api/portfolioAdmin/reportRequestsApi";

export type TimelineEntryType =
  | "event"
  | "note"
  | "cure-period"
  | "remediation-plan"
  | "forbearance"
  | "daca"
  | "legal-action"
  | "engagement"
  | "article-9"
  | "cost"
  | "recovery"
  | "report-request"
  | "closure";

export const TIMELINE_TYPE_LABELS: Record<TimelineEntryType, string> = {
  event: "Events",
  note: "Notes",
  "cure-period": "Cure periods",
  "remediation-plan": "Remediation plans",
  forbearance: "Forbearance",
  daca: "DACA",
  "legal-action": "Legal actions",
  engagement: "Enforcer engagements",
  "article-9": "Article 9",
  cost: "Costs",
  recovery: "Recoveries",
  "report-request": "Report requests",
  closure: "Closure",
};

export type TimelineBadgeStatus =
  | "info"
  | "draft"
  | "review"
  | "gold"
  | "error"
  | "finalized";

export interface CaseTimelineEntry {
  /** Deep-link key: `<type>:<artifactId>` (the `?artifact=` param value). */
  key: string;
  type: TimelineEntryType;
  artifactId: string;
  date: string;
  title: string;
  detail?: string;
  amount?: number | null;
  /** Per-entry badge override (e.g. an event's category with its severity
   *  tone — the old Event Stream's CATEGORY_BADGE map); falls back to the
   *  per-type badge when absent. */
  badge?: { label: string; status: TimelineBadgeStatus };
  /** Out-of-band attention line (event follow-ups) — rendered warning-
   *  tinted in the list so a scheduled follow-up is visible without
   *  opening the drawer. */
  attention?: string;
}

/**
 * Tone per report-request status. Keyed on `string` rather than the status
 * union because that union is deliberately widened server-side — an
 * unrecognised status renders as itself with the neutral tone rather than
 * crashing a lookup.
 */
export const REPORT_REQUEST_BADGE: Record<string, TimelineBadgeStatus> = {
  issued: "info",
  "in-progress": "info",
  "under-review": "review",
  complete: "gold",
  reported: "finalized",
  cancelled: "draft",
};

/** The old Event Stream's category severity map, carried onto timeline
 *  event entries so monetary/legal/covenant events keep their tone. */
export const EVENT_CATEGORY_BADGE: Record<string, TimelineBadgeStatus> = {
  "monetary-default": "error",
  "covenant-default": "review",
  "technical-default": "review",
  "workout-action": "gold",
  "borrower-action": "info",
  "legal-event": "review",
  "document-event": "draft",
  "financial-event": "finalized",
  "state-transition": "info",
  note: "draft",
};

export interface CaseArtifacts {
  defaultCase: DefaultCaseDto;
  events: WorkoutEventDto[];
  curePeriods: CurePeriodDto[];
  plans: RemediationPlanDto[];
  forbearance: ForbearanceAgreementDto[];
  daca: DACAEventDto[];
  legalActions: LegalActionDto[];
  engagements: EnforcerEngagementDto[];
  collateralRecoveries: CollateralRecoveryDto[];
  costs: WorkoutCostEntryDto[];
  recoveries: WorkoutRecoveryEntryDto[];
  /**
   * The LOAN's report requests, unfiltered. `buildCaseTimeline` narrows them
   * to this case itself — see `reportRequestsForCase`. Required rather than
   * optional on purpose: an optional list is one a second mount point can
   * forget to pass, and the symptom would be a case timeline that silently
   * omits the reports B3 asked the borrower for.
   */
  reportRequests: ReportRequestDto[];
}

/**
 * The report requests belonging to ONE case: Default/Workout type AND stamped
 * with this case's id.
 *
 * <p>🔴 The narrowing lives HERE, not at the caller. `ReportRequestService`
 * only stamps `defaultCaseId` for `DefaultWorkout` requests, so a type check
 * alone would be right today and wrong the moment another type starts carrying
 * a case; and an id check alone would let a Quarterly request that happened to
 * be stamped leak onto a workout timeline. Both, together, in the one place
 * every caller goes through.</p>
 */
export function reportRequestsForCase(
  requests: ReportRequestDto[],
  caseId: string,
): ReportRequestDto[] {
  return requests.filter(
    (r) => r.requestType === "DefaultWorkout" && r.defaultCaseId === caseId,
  );
}

/**
 * A request whose deadline has passed and which somebody could still act on.
 *
 * <p>🔴 THE ONE OVERDUE RULE. There used to be two — this one, and a private
 * `isOverdue` in the Financial Reporting tab's `ReportRequestList` — and they
 * disagreed about the DUE DAY ITSELF. That one compared
 * `new Date(dueDate).getTime() < Date.now()`; `dueDate` is a wall-calendar
 * value stored at UTC midnight, so it read as overdue from the first moment of
 * the day the request was due, and a viewer at a negative UTC offset saw it go
 * red the evening BEFORE. The wall-calendar rule survived, and both surfaces
 * now agree.</p>
 *
 * <p>🔴 IT DELEGATES TO `isPastCalendarDay`, AND THE CORRECTION THAT MADE
 * NECESSARY IS THE `now` SIDE, NOT THE `dueDate` SIDE. This doc block used to
 * claim that comparing the leading `YYYY-MM-DD` of two ISO strings "needs no
 * timezone arithmetic at all". That is true of the DUE date — a day somebody
 * picked, which means the same day everywhere — and FALSE of `now`, which is a
 * real instant, so "what day is it" is a question about where the VIEWER is
 * standing. `now.toISOString()` answers it in UTC. America/Phoenix is UTC-7 all
 * year, so from 17:00 local every day the UTC day had already rolled over and a
 * request due TODAY was painted overdue for the last seven hours of the day it
 * was actually due — on the Financial Reporting list, in the lifecycle drawer,
 * on the case timeline and in the overdue count, all at once, because they
 * share this one rule. `isPastCalendarDay` reads the two sides in their two
 * different zones for exactly that reason; see its own doc block.</p>
 *
 * <p>`tz` is a PARAMETER and never a hook. This is a pure module function, and
 * two of its callers (`overdueRequests` and `buildCaseTimeline`) are pure too,
 * so the viewer's zone is threaded down from whichever component holds it —
 * `ReportRequestList` and `ReportRequestLifecycleDrawer` already take it as a
 * prop, and `ArtifactDrawer` already destructures it from `useDateFormatter`.
 * None of the three needed a new hook; using the tz already in scope is also
 * what stops a surface disagreeing with its own date column.</p>
 *
 * <p>It is OPTIONAL, matching every tz-taking helper in `format.ts`: omitted,
 * it falls back to `FALLBACK_TIMEZONE` (America/Phoenix), which mirrors the
 * server's `IUserTimeZoneResolver` and is right for everyone who has not set
 * another zone. Making it required was rejected — it buys nothing for a viewer
 * in the default zone, and it would force a `tz` on callers that do not hold
 * one. 🔴 The consequence, stated rather than hidden: two component call sites
 * are NOT yet threaded and take that fallback — `ReportRequestsSection` (which
 * has the viewer's zone in scope and simply does not pass it on to
 * `overdueRequests`) and `DefaultsWorkoutTab` (which does not read it at all).
 * Both are strictly better than the UTC comparison they replace; both are
 * wrong for a viewer who has set a non-Phoenix zone.</p>
 *
 * <p>The FINISHED check is the half the list carried and this one did not.
 * Nobody owes anything on a cancelled, reported or complete request, so its
 * deadline stops meaning anything the moment it reaches one of those states.
 * Folding it in here rather than leaving it to each caller is what makes this
 * a single rule instead of a shared fragment of one — the timeline's callers
 * additionally require `outstandingCount > 0`, which is stricter, so the only
 * behaviour that moves is a CANCELLED request with items still outstanding: it
 * used to be labelled overdue on the case timeline, and a request nobody is
 * being asked to satisfy is not late.</p>
 */
const FINISHED_REQUEST_STATUSES: readonly string[] = [
  ...TERMINAL_REQUEST_STATUSES,
  "complete",
];

export function isReportRequestOverdue(
  request: ReportRequestDto,
  now: Date = new Date(),
): boolean {
  if (FINISHED_REQUEST_STATUSES.includes(request.status)) return false;
  return isPastCalendarDay(request.dueDate, now);
}

export function timelineKey(type: TimelineEntryType, artifactId: string): string {
  return `${type}:${artifactId}`;
}

export function parseTimelineKey(
  key: string | null,
): { type: TimelineEntryType; artifactId: string } | null {
  if (!key) return null;
  const idx = key.indexOf(":");
  if (idx <= 0 || idx === key.length - 1) return null;
  const type = key.slice(0, idx) as TimelineEntryType;
  if (!(type in TIMELINE_TYPE_LABELS)) return null;
  return { type, artifactId: key.slice(idx + 1) };
}

function entry(
  type: TimelineEntryType,
  artifactId: string,
  date: string,
  title: string,
  detail?: string,
  amount?: number | null,
  extra?: Pick<CaseTimelineEntry, "badge" | "attention">,
): CaseTimelineEntry {
  return {
    key: timelineKey(type, artifactId),
    type,
    artifactId,
    date,
    title,
    detail,
    amount,
    ...extra,
  };
}

/**
 * Folds every artifact into typed entries, newest first (stable tiebreak
 * on key so equal timestamps don't jitter between renders).
 *
 * `tz` is threaded, not hooked — this is a pure function, and its only use for
 * the viewer's zone is the overdue mark on a report-request entry. Omitted, it
 * takes `isReportRequestOverdue`'s own America/Phoenix fallback.
 */
export function buildCaseTimeline(
  a: CaseArtifacts,
): CaseTimelineEntry[] {
  const entries: CaseTimelineEntry[] = [];

  for (const e of a.events) {
    const isNote = e.eventCategory === "note";
    entries.push(entry(
      isNote ? "note" : "event",
      e.id, e.eventDate,
      isNote ? "Note" : e.eventSubtype,
      e.description, e.dollarImpact,
      {
        badge: isNote
          ? undefined
          : {
              label: e.eventCategory,
              status: EVENT_CATEGORY_BADGE[e.eventCategory] ?? "info",
            },
        attention: e.followUpDate
          ? `Follow-up: ${e.followUpDate.slice(0, 10)}${
              e.followUpAssignedTo ? ` → ${e.followUpAssignedTo}` : ""
            }`
          : undefined,
      }));
  }
  for (const cp of a.curePeriods) {
    entries.push(entry(
      "cure-period", cp.id, cp.startDate,
      `Cure period started (${cp.curePeriodDays} days)`,
      cp.cureAchieved ? "Cured" : `Ends ${(cp.extendedEndDate ?? cp.endDate).slice(0, 10)}`));
  }
  for (const p of a.plans) {
    entries.push(entry(
      "remediation-plan", p.id, p.startDate,
      `Remediation plan — Level ${p.level}`,
      `${p.title} — ${p.status}`));
  }
  for (const f of a.forbearance) {
    entries.push(entry(
      "forbearance", f.id, f.agreementDate,
      `Forbearance ${f.status}`,
      f.forbearanceType, f.forbearanceAmount));
  }
  for (const d of a.daca) {
    entries.push(entry(
      "daca", d.id, d.activationDate,
      `DACA ${d.status}`,
      `Monthly collection ${formatCurrency(d.monthlyCollectionAmount)}`));
  }
  for (const l of a.legalActions) {
    entries.push(entry(
      "legal-action", l.id, l.initiatedDate,
      `Legal action — ${l.actionType}`,
      l.status, l.settlementAmount));
  }
  for (const e of a.engagements) {
    entries.push(entry(
      "engagement", e.id, e.engagementStartDate,
      `Enforcer engaged — ${e.engagementType}`,
      `${e.feeStructure} — ${e.status}`, e.feeAmount));
  }
  for (const r of a.collateralRecoveries) {
    entries.push(entry(
      "article-9", r.id, r.recoveryDate ?? a.defaultCase.triggerDate,
      `Article 9 — ${r.recoveryMethod}`,
      `${r.collateralType} — ${r.status}`, r.recoveryAmount));
  }
  for (const c of a.costs) {
    const approved = !!c.amountApprovedDate;
    entries.push(entry(
      "cost", c.id, c.incurredDate,
      `Cost — ${c.costCategory}`,
      (c.recoverableFromBorrower ? "Recoverable from borrower" : "Non-recoverable") +
        (approved ? "" : " • pending approval"),
      c.amount,
      approved ? undefined : { attention: "Pending approval" }));
  }
  for (const r of a.recoveries) {
    entries.push(entry(
      "recovery", r.id, r.recoveryDate,
      `Recovery — ${r.recoverySource}`,
      `Applied to ${r.appliedToWaterfall}`, r.netAmount));
  }
  // Report requests B3 issued against THIS case. Anchored on `issuedAt` — a
  // genuine instant, and the moment the thing happened, which is what a
  // chronological spine orders by. The period and the due date are
  // wall-calendar values and go in the detail line through
  // `formatCalendarDate`, never the tz-aware formatter the row's date column
  // uses.
  for (const r of reportRequestsForCase(a.reportRequests, a.defaultCase.id)) {
    const overdue = isReportRequestOverdue(r) && r.outstandingCount > 0;
    entries.push(entry(
      "report-request", r.id, r.issuedAt,
      `${r.requestTypeLabel} report — ${r.periodLabel}`,
      `Due ${formatCalendarDate(r.dueDate)} · ${r.approvedCount} of ${r.itemCount} items approved`,
      null,
      {
        badge: {
          label: REPORT_REQUEST_STATUS_LABEL[r.status] ?? r.status,
          status: REPORT_REQUEST_BADGE[r.status] ?? "info",
        },
        // Overdue is stated in words rather than left to the badge tone: the
        // badge already carries the request's own status, and "waiting for
        // your review" beside a red pill would read as the REVIEW being late.
        attention: overdue
          ? `Overdue — ${r.outstandingCount} item${r.outstandingCount === 1 ? "" : "s"} still outstanding`
          : undefined,
      }));
  }
  if (a.defaultCase.closedDate) {
    entries.push(entry(
      "closure", a.defaultCase.id, a.defaultCase.closedDate,
      "Case closed",
      a.defaultCase.resolutionType ?? undefined));
  }

  return entries.sort((x, y) => {
    const d = new Date(y.date).getTime() - new Date(x.date).getTime();
    return d !== 0 ? d : x.key.localeCompare(y.key);
  });
}
